/**
 * 16回以上施術理由 編集 - Long Term Frequent Edit Page
 * Switches between A2 (はり・きゅう) and R2 (マッサージ) forms based on service type.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  AppButton,
  AppCard,
  AppForm,
  AppFormLabel,
  AppSelect,
  AppTextField,
  AppTextArea,
  AppSpin,
  AppRadio,
  AppRadioGroup,
  AppCheckbox,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import {
  useLongTermFrequentOne,
  useLongTermFrequentUpdate,
  useLongTermFrequentDelete,
  useLongTermFrequentMonthData,
  useLongTermFrequentMasterData,
} from '@/hooks/main/useLongTermFrequentEdit';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  DELETE_COMPLETED_MESSAGE,
  DELETE_CONFIRM_DATA_MESSAGE,
  DELETE_ERROR_MESSAGE,
  PDF_ERROR_MESSAGE,
} from '@/constants/messages';
import {
  STANDARD_DELETE_CONFIRM_TITLE,
  STANDARD_DELETE_CONFIRM_OK_TEXT,
  STANDARD_DELETE_CONFIRM_CANCEL_TEXT,
} from '@/constants/deleteConfirm';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import {
  longTermFrequentA2Schema,
  longTermFrequentR2Schema,
  type LongTermFrequentA2FormData,
  type LongTermFrequentR2FormData,
} from '@/schemas/longTermFrequentSchema';
import { TENANT_ROUTES } from '@/constants/routes';
import { FORM_MESSAGES } from '@/constants/formMessages';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import {
  A2_DISEASE_NAME_OPTIONS,
  A2_DISEASE_OTHER_ID,
  R2_MASSAGE_PART_OPTIONS,
} from '@/constants/consentForm';
import { fetchLastVisitDate, fetchLongTermFrequentPdf } from '@/services/longTermFrequentService';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { convGtJDate, yearmonthToDateStr, convGtJMonth } from '@/utils/dateConverter';
import { convertSeirekiWrap } from '@/utils/wareki';
import { resolveR2TreatmentPartLabelsFromStoredConsent } from '@/utils/consentForm';
import { isNotFoundError } from '@/utils/apiError';
import { NotFound } from '@/pages/main/system/NotFound';

type FormData = LongTermFrequentA2FormData | LongTermFrequentR2FormData;

const NOTE_TEXT =
  '※傷病名～初療年月日は同意書の内容が反映されます。変更する場合は同意書の修正を行って下さい';

const onWarekiDateChange = (fieldOnChange: (v: string) => void) => (warekiStr: string) => {
  if (!warekiStr?.trim()) {
    fieldOnChange('');
    return;
  }
  const seireki = convertSeirekiWrap(warekiStr);
  const d = dayjs(seireki.replace(/\//g, '-'));
  fieldOnChange(d.isValid() ? d.format('YYYY-MM-DD') : '');
};

function deriveTreatmentTypeLabel(content: Array<string | number>): string {
  if (!content || content.length === 0) return '';
  const normalized = content.map(v => String(v).trim());
  const hasHari = normalized.some(v => v === 'はり' || v === '1');
  const hasKyu = normalized.some(v => v === 'きゅう' || v === '3');
  if (hasHari && hasKyu) return 'はり・きゅう併用';
  if (hasHari) return 'はり';
  if (hasKyu) return 'きゅう';
  return '';
}

const LongTermFrequentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    tenant,
    id: patientId,
    recordId,
  } = useParams<{
    tenant?: string;
    id?: string;
    recordId?: string;
  }>();
  const { serviceType: contextServiceType, colors } = useTheme();
  const { tenant: siteType, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const sidebarOpen = useSidebarOpen();

  const isA2 = (contextServiceType?.toUpperCase() ?? '') === SERVICE_TYPE_A2;
  const isR2 = !isA2;
  const schema = isA2 ? longTermFrequentA2Schema : longTermFrequentR2Schema;

  // Fetch existing record
  const {
    data: record,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useLongTermFrequentOne(patientId, recordId);

  // Build default values from existing record
  const defaultValues = useMemo(() => {
    if (!record) return undefined;
    if (isA2) {
      return {
        visit_yearmonth: String(record.visit_yearmonth ?? ''),
        valuation_date: record.valuation_date ?? '',
        degree_of_pain: record.degree_of_pain ?? 5,
        recovery_type_id: record.recovery_type_id ?? null,
        massager_id: record.massager_id ?? null,
        treatment_continuation_reason: record.treatment_continuation_reason ?? '',
        create_document_date: record.create_document_date ?? '',
      };
    }
    return {
      visit_yearmonth: String(record.visit_yearmonth ?? ''),
      valuation_date: record.valuation_date ?? '',
      valuation_betray_type_id: record.valuation_betray_type_id ?? 1,
      valuation_sit_up_type_id: record.valuation_sit_up_type_id ?? 1,
      valuation_sitting_type_id: record.valuation_sitting_type_id ?? 1,
      valuation_stand_up_type_id: record.valuation_stand_up_type_id ?? 1,
      valuation_standing_type_id: record.valuation_standing_type_id ?? 1,
      recovery_type_id: record.recovery_type_id ?? null,
      massager_id: record.massager_id ?? null,
      treatment_continuation_reason: record.treatment_continuation_reason ?? '',
      create_document_date: record.create_document_date ?? '',
    };
  }, [record, isA2]);

  const { control, handleSubmit, watch, setValue, setError, reset } = useTrimmedForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as any,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  // Reset form when record loads
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues as any);
    }
  }, [defaultValues, reset]);

  const currentValues = watch();
  const visitYearmonth = watch('visit_yearmonth');
  const { hasFormChanged, clearFormChange } = useFormChangeGuard(currentValues, defaultValues);

  const updateMutation = useLongTermFrequentUpdate(patientId || '');
  const deleteMutation = useLongTermFrequentDelete(patientId);
  const toast = useAppToast();
  const { confirm } = useAppModal();

  // Patient info
  const { data: patient } = useMainPatient(patientId ? Number(patientId) : undefined);

  // Fetch master data
  const { recoveryTypes, valuationTypes } = useLongTermFrequentMasterData(patientId);

  // Fetch month data (consent + visits + prev month)
  const { data: monthData } = useLongTermFrequentMonthData(patientId, visitYearmonth);
  const isPrev = monthData?.is_prev ?? false;
  const prevRecoveryId = monthData?.prev_recovery_type_id ?? null;
  // 前月の評価 display: A2 needs both prev existence + recovery_type_id; R2 needs prev existence only.
  const showHasPrev = isA2 ? isPrev && prevRecoveryId != null : isPrev;

  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!record || !monthData || record.massager_id != null) {
      return;
    }

    setValue('massager_id' as any, monthData.default_massager_id ?? null);
  }, [record, monthData, setValue]);

  const listUrl = TENANT_ROUTES.LONG_TERM_FREQUENTS.LIST(tenant || '', patientId || '');
  const isBusy = updateMutation.isPending || deleteMutation.isPending || isPrinting;

  const handleBack = useCallback(() => {
    if (isBusy) return;
    if (hasFormChanged) {
      if (!window.confirm(FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION)) return;
    }
    clearFormChange();
    authorizeNavigation();
    navigate(listUrl);
  }, [clearFormChange, navigate, listUrl, isBusy, hasFormChanged]);

  const handleDelete = useCallback(() => {
    if (isBusy || !recordId) return;
    confirm({
      title: STANDARD_DELETE_CONFIRM_TITLE,
      content: DELETE_CONFIRM_DATA_MESSAGE,
      okButtonProps: { danger: true },
      okText: STANDARD_DELETE_CONFIRM_OK_TEXT,
      cancelText: STANDARD_DELETE_CONFIRM_CANCEL_TEXT,
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(Number(recordId));
          clearFormChange();
          authorizeNavigation();
          toast.success(DELETE_COMPLETED_MESSAGE);
          navigate(listUrl, { replace: true });
        } catch (err: unknown) {
          toast.error(getApiErrorMessage(err, DELETE_ERROR_MESSAGE));
        }
      },
    });
  }, [isBusy, recordId, confirm, deleteMutation, clearFormChange, toast, navigate, listUrl]);

  const handlePrint = useCallback(async () => {
    if (!recordId || !patientId || isBusy) return;
    setIsPrinting(true);
    try {
      const blob = await fetchLongTermFrequentPdf(
        siteType,
        clinicNumber,
        patientId,
        Number(recordId)
      );
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, PDF_ERROR_MESSAGE));
    } finally {
      setIsPrinting(false);
    }
  }, [recordId, patientId, siteType, clinicNumber, isBusy, toast]);

  const handleGetLastVisitDate = useCallback(async () => {
    if (!visitYearmonth || !patientId) return;
    try {
      const res = await fetchLastVisitDate(siteType, clinicNumber, patientId, visitYearmonth);
      if (res?.data?.last_visit_date) {
        setValue('create_document_date', res.data.last_visit_date);
      }
    } catch {
      // No visit found - keep current value
    }
  }, [visitYearmonth, patientId, siteType, clinicNumber, setValue]);

  const applyApiFieldErrors = useCallback(
    (error: unknown) => {
      const err = error as {
        response?: { status?: number; data?: { errors?: Record<string, string[]> } };
      };
      if (err.response?.status === 422 && err.response.data?.errors) {
        const apiErrors = err.response.data.errors;
        Object.keys(apiErrors).forEach(key => {
          setError(key as any, {
            type: 'manual',
            message: Array.isArray(apiErrors[key]) ? apiErrors[key][0] : String(apiErrors[key]),
          });
        });
      }
    },
    [setError]
  );

  const handleUpdateSuccess = useCallback(
    (response: any) => {
      if (!response?.success) return;
      clearFormChange();
      authorizeNavigation();
      setTimeout(() => {
        navigate(listUrl, { replace: true });
        queryClient.removeQueries({ queryKey: ['long-term-frequents', 'one'] });
        queryClient.removeQueries({ queryKey: ['long-term-frequents', 'list'] });
      }, 300);
    },
    [clearFormChange, navigate, listUrl, queryClient]
  );

  const handleUpdateError = useCallback(
    (error: unknown) => {
      if (isNotFoundError(error)) {
        navigate(listUrl, { replace: true });
        return;
      }
      applyApiFieldErrors(error);
    },
    [navigate, listUrl, applyApiFieldErrors]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!recordId) return;
      const payload = {
        ...data,
        recovery_type_id: data.recovery_type_id ?? undefined,
      };
      updateMutation.mutate(
        { id: Number(recordId), payload },
        { onSuccess: handleUpdateSuccess, onError: handleUpdateError }
      );
    },
    [updateMutation, recordId, handleUpdateSuccess, handleUpdateError]
  );

  const onInvalid = useCallback(() => {
    toast.error('更新ができませんでした。');
  }, [toast]);

  // Derived read-only consent display values
  const checkedDiseases: string[] = Array.isArray(monthData?.disease_name)
    ? monthData.disease_name
    : [];
  const diseaseNameOther = monthData?.disease_name_other ?? '';
  const treatmentTypeLabel = deriveTreatmentTypeLabel(
    Array.isArray(monthData?.treatment_content) ? monthData.treatment_content : []
  );
  const firstTreatmentDateDisplay = monthData?.first_treatment_date
    ? convGtJDate(dayjs(monthData.first_treatment_date))
    : '';
  const visitCountDisplay = monthData?.visit_count ?? 0;
  const massagerOptions = useMemo(
    () => (monthData?.massagers ?? []).map(m => ({ value: m.id, label: m.name })),
    [monthData?.massagers]
  );
  const r2SelectedTreatmentPartLabels = useMemo(
    () =>
      isR2
        ? resolveR2TreatmentPartLabelsFromStoredConsent(monthData?.massage, monthData?.orthomorphia)
        : new Set<string>(),
    [isR2, monthData?.massage, monthData?.orthomorphia]
  );

  // Loading / error states
  if (isDetailLoading) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  if (isDetailError || !record) {
    return <NotFound />;
  }

  return (
    <div className="patient-edit-page">
      <PatientPageHeader
        patient={patient}
        patientId={patientId || ''}
        tenant={tenant || ''}
        navigate={navigate}
        activeKey="long-term-frequents"
        disabled={isBusy}
      />

      <div>
        <form id="long-term-frequent-edit-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <AppCard
            className="bg-[#fdf5e6]"
            style={{
              boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
            }}
          >
            <span className="text-sm text-blue-600">{NOTE_TEXT}</span>

            <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-x-6 gap-y-4 items-start mt-4">
              {/* ① 施術年月 — disabled in edit mode */}
              <AppFormLabel label="施術年月" required />
              <Controller
                name="visit_yearmonth"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <RA2NDatePicker
                      picker="month"
                      placeholder="年月指定"
                      className="w-52"
                      value={
                        field.value
                          ? convGtJMonth(dayjs(yearmonthToDateStr(Number(field.value))))
                          : ''
                      }
                      disabled
                      error={!!error}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ===== A2-specific read-only consent fields ===== */}
              {isA2 && (
                <>
                  {/* ② 傷病名 */}
                  <AppFormLabel label="傷病名" />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {A2_DISEASE_NAME_OPTIONS.map(opt =>
                      opt.value === A2_DISEASE_OTHER_ID ? (
                        <div key={opt.value} className="flex items-center gap-1">
                          <AppCheckbox checked={checkedDiseases.includes(opt.value)} disabled>
                            {opt.label}
                          </AppCheckbox>
                          <span>(</span>
                          <AppTextField value={diseaseNameOther} disabled className="w-48" />
                          <span>)</span>
                        </div>
                      ) : (
                        <AppCheckbox
                          key={opt.value}
                          checked={checkedDiseases.includes(opt.value)}
                          disabled
                        >
                          {opt.label}
                        </AppCheckbox>
                      )
                    )}
                  </div>

                  {/* ③ 施術の種類 */}
                  <AppFormLabel label="施術の種類" />
                  <div>
                    <AppRadioGroup value={treatmentTypeLabel || undefined} disabled>
                      <AppRadio value="はり">はり</AppRadio>
                      <AppRadio value="きゅう">きゅう</AppRadio>
                      <AppRadio value="はり・きゅう併用">はり・きゅう併用</AppRadio>
                    </AppRadioGroup>
                  </div>

                  {/* ④ 初療年月日 */}
                  <AppFormLabel label="初療年月日" />
                  <AppTextField value={firstTreatmentDateDisplay} disabled className="w-52" />

                  {/* ⑤ 施術回数 */}
                  <AppFormLabel label="施術回数" />
                  <div className="flex items-center gap-1">
                    <AppTextField value={String(visitCountDisplay)} disabled className="w-20" />
                    <span>回</span>
                  </div>
                </>
              )}

              {/* ===== R2-specific read-only consent fields ===== */}
              {isR2 && (
                <>
                  <AppFormLabel label="傷病名" />
                  <AppTextField
                    value={
                      monthData && typeof monthData.disease_name === 'string'
                        ? monthData.disease_name
                        : Array.isArray(monthData?.disease_name)
                          ? monthData.disease_name.join(', ')
                          : ''
                    }
                    disabled
                    className="w-1/2"
                  />

                  <AppFormLabel label="症状" />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <AppCheckbox
                      checked={
                        !!monthData?.has_consent &&
                        !monthData.symptom_muscle &&
                        !monthData.symptom_joint &&
                        !monthData.symptom_other
                      }
                      disabled
                    >
                      なし
                    </AppCheckbox>
                    <AppCheckbox checked={!!monthData?.symptom_muscle} disabled>
                      筋麻痺
                    </AppCheckbox>
                    <AppCheckbox checked={!!monthData?.symptom_joint} disabled>
                      関節拘縮
                    </AppCheckbox>
                    <div className="flex items-center gap-1">
                      <AppCheckbox checked={!!monthData?.symptom_other} disabled>
                        その他
                      </AppCheckbox>
                      <span>(</span>
                      <AppTextField
                        value={monthData?.symptom_other ?? ''}
                        disabled
                        className="w-48"
                      />
                      <span>)</span>
                    </div>
                  </div>

                  <AppFormLabel label="施術の種類" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <AppCheckbox checked={!!monthData?.massage} disabled>
                      マッサージ
                    </AppCheckbox>
                    <AppCheckbox checked={!!monthData?.orthomorphia} disabled>
                      変形徒手矯正
                    </AppCheckbox>
                  </div>

                  <AppFormLabel label="施術部位" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {R2_MASSAGE_PART_OPTIONS.map(opt => (
                      <AppCheckbox
                        key={opt.value}
                        checked={r2SelectedTreatmentPartLabels.has(opt.value)}
                        disabled
                      >
                        {opt.label}
                      </AppCheckbox>
                    ))}
                  </div>

                  <AppFormLabel label="初療年月日" />
                  <AppTextField value={firstTreatmentDateDisplay} disabled className="w-52" />

                  <AppFormLabel label="施術回数" />
                  <div className="flex items-center gap-1">
                    <AppTextField value={String(visitCountDisplay)} disabled className="w-20" />
                    <span>回</span>
                  </div>
                </>
              )}

              {/* ⑥ 患者状態評価日 */}
              <AppFormLabel label="患者状態評価日" required />
              <Controller
                name="valuation_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <RA2NDatePicker
                      placeholder="元号00年00月00日"
                      className="w-52"
                      value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                      onChange={onWarekiDateChange(field.onChange)}
                      error={!!error}
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ⑦ A2: 痛みの強さ */}
              {isA2 && (
                <>
                  <AppFormLabel label="痛みの強さ" required />
                  <Controller
                    name={'degree_of_pain' as any}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <AppForm.Item
                        validateStatus={error ? 'error' : undefined}
                        help={error?.message}
                        className="mb-0"
                      >
                        <AppRadioGroup
                          value={field.value}
                          onChange={e => field.onChange(Number(e.target.value))}
                          disabled={isBusy}
                        >
                          {Array.from({ length: 11 }, (_, i) => i).map(val => (
                            <AppRadio key={val} value={val}>
                              {val}
                            </AppRadio>
                          ))}
                        </AppRadioGroup>
                        <div className="mt-1 text-sm text-blue-600">
                          (注)全く痛みが無い状態を「0」、自分が考え想像しうる最悪の傷みを「10」として今感じている痛みの点数を患者様に聞き、該当の点数をつけてください。
                        </div>
                      </AppForm.Item>
                    )}
                  />
                </>
              )}

              {/* R2: 5 evaluation fields */}
              {isR2 &&
                (
                  [
                    { name: 'valuation_betray_type_id', label: '寝返り' },
                    { name: 'valuation_sit_up_type_id', label: '起き上がり' },
                    { name: 'valuation_sitting_type_id', label: '座位' },
                    { name: 'valuation_stand_up_type_id', label: '立ち上がり' },
                    { name: 'valuation_standing_type_id', label: '立位' },
                  ] as const
                ).map(({ name, label }) => (
                  <React.Fragment key={name}>
                    <AppFormLabel label={label} required />
                    <Controller
                      name={name as any}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <AppForm.Item
                          validateStatus={error ? 'error' : undefined}
                          help={error?.message}
                          className="mb-0"
                        >
                          <AppRadioGroup
                            value={field.value}
                            onChange={e => field.onChange(Number(e.target.value))}
                            disabled={isBusy}
                          >
                            {valuationTypes.map(vt => (
                              <AppRadio key={vt.id} value={vt.id}>
                                {vt.name}
                              </AppRadio>
                            ))}
                          </AppRadioGroup>
                        </AppForm.Item>
                      )}
                    />
                  </React.Fragment>
                ))}

              {/* ⑧ 前月の評価 */}
              <AppFormLabel label="前月の評価" />
              <div>
                <span className={showHasPrev ? 'text-green-700 font-medium' : 'text-gray-500'}>
                  {showHasPrev ? '有り' : '無し'}
                </span>
                {showHasPrev && monthData?.prev_recovery_type_name && (
                  <span className="ml-2 text-gray-600">({monthData.prev_recovery_type_name})</span>
                )}
              </div>

              {/* ⑨ 改善状況 */}
              <AppFormLabel label="改善状況" />
              <Controller
                name="recovery_type_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppRadioGroup
                      value={field.value ?? undefined}
                      onChange={e => field.onChange(Number(e.target.value))}
                      disabled={isBusy || !isPrev}
                    >
                      {recoveryTypes.map(rt => (
                        <AppRadio key={rt.id} value={rt.id}>
                          {rt.name}
                        </AppRadio>
                      ))}
                    </AppRadioGroup>
                  </AppForm.Item>
                )}
              />

              {/* ⑩ 施術が必要な理由 */}
              <AppFormLabel label="施術が必要な理由" required />
              <Controller
                name="treatment_continuation_reason"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppTextArea
                      value={field.value || ''}
                      onChange={e => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      rows={4}
                      maxLength={1000}
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ⑪ はり・きゅう師 / マッサージ師 */}
              <AppFormLabel label={isA2 ? 'はり・きゅう師' : 'マッサージ師'} />
              <Controller
                name="massager_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppSelect
                      className="w-52"
                      value={field.value ?? undefined}
                      onChange={value =>
                        field.onChange(value != null && value !== '' ? Number(value) : null)
                      }
                      options={massagerOptions}
                      notFoundContent="データがありません"
                      placeholder="選択してください"
                      disabled={isBusy}
                    />
                  </AppForm.Item>
                )}
              />

              {/* ⑫ 作成日 + 最終施術日取得 */}
              <AppFormLabel label="作成日" required />
              <Controller
                name="create_document_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <div className="flex items-start gap-2">
                      <RA2NDatePicker
                        placeholder="元号00年00月00日"
                        className="w-52"
                        value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                        onChange={onWarekiDateChange(field.onChange)}
                        error={!!error}
                        disabled={isBusy}
                      />
                      <AppButton type="primary" onClick={handleGetLastVisitDate} disabled={isBusy}>
                        最終施術日取得
                      </AppButton>
                    </div>
                  </AppForm.Item>
                )}
              />
            </div>
          </AppCard>
        </form>
      </div>

      <BottomBar
        variant="action"
        visible
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <div className="flex gap-2">
            <AppButton
              type="primary"
              disabled={isBusy}
              loading={isPrinting}
              onClick={handlePrint}
              className="px-4 py-2 font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-green-600 disabled:text-white disabled:opacity-50"
            >
              月16回記入書印刷
            </AppButton>
          </div>
        }
        right={
          <div className="flex gap-4">
            <AppButton
              type="primary"
              disabled={isBusy}
              loading={deleteMutation.isPending}
              onClick={handleDelete}
              icon={<i className="fa fa-trash-o" aria-hidden="true" />}
              className="px-4 py-2 font-medium text-white bg-blue-400 rounded hover:!bg-blue-500 disabled:bg-blue-300 disabled:text-white disabled:cursor-not-allowed"
            >
              削除
            </AppButton>
            <AppButton
              type="default"
              onClick={handleBack}
              disabled={isBusy}
              className="px-4 py-2 font-medium rounded"
            >
              一覧に戻る
            </AppButton>
            <AppButton
              type="primary"
              htmlType="submit"
              form="long-term-frequent-edit-form"
              loading={updateMutation.isPending}
              disabled={!updateMutation.isPending && isBusy}
              className="px-12 py-2 font-medium text-white rounded"
              style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
            >
              <i className="mr-2 fa fa-paper-plane" aria-hidden="true" /> 更新
            </AppButton>
          </div>
        }
      />
    </div>
  );
};

export default LongTermFrequentEditPage;
